import { useState, useEffect, useRef, useContext, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json';
import { getTransitionTargets, getStateLabel } from '../kanban/kanbanWorkflowStore';
import { subscribePolicy, getPolicyVersion } from '../kanban/kanbanTransitionPolicy';

/**
 * TaskStateDropdown — la pastille d'état d'une tâche, cliquable, qui déroule les
 * transitions atteignables.
 *
 * EXTRAIT de KanbanListRow (où il s'appelait StatusCell) pour être partagé, et
 * non recopié : la vue liste du Kanban et le bloc « Tâches » des fiches CRM
 * rendent désormais le MÊME contrôle, avec le même pré-filtrage (atteignable ∩
 * autorisé par le rôle ∩ sous-tâches closes) et le même événement de sortie.
 * Choisir une cible n'écrit jamais l'état en direct : l'événement
 * `kanbanTransitionRequest` est émis, et c'est le board — ou, hors board,
 * TaskTransitionBridge — qui persiste, de sorte que la validation serveur du
 * workflow reste sur le chemin.
 *
 * Sans cible atteignable, l'état reste affiché en pastille inerte.
 *
 * `columns` n'existe que sur le board (repli de libellé sur le titre de la
 * colonne) : optionnel, et vide ailleurs.
 */
// and the row isn't archived, a click opens a dropdown that dispatches the
// existing kanbanTransitionRequest event — server workflow validation preserved,
// never a raw columnId/state write. Otherwise a static badge.
export const TaskStateDropdown = ({ taskId, typeId, state, columns = [], columnId, isArchived }) => {
    // La politique vit hors de React et se sème en asynchrone : sans cet
    // abonnement, la pastille calcule ses cibles trop tôt et reste inerte
    // jusqu'au prochain rendu provoqué par autre chose.
    useSyncExternalStore(subscribePolicy, getPolicyVersion, getPolicyVersion);
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const close = () => setOpen(false);
        // Ignore the button + the portaled menu so mousedown doesn't close the menu
        // before an option's click fires (same contract as PriorityDropdown).
        const onDoc = (e) => {
            if (btnRef.current?.contains(e.target)) return;
            if (menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        document.addEventListener('mousedown', onDoc);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
            document.removeEventListener('mousedown', onDoc);
        };
    }, [open]);

    // Label from the row's OWN workflow first; then the board column title; then
    // the raw state id (so a real-but-unregistered state is never shown as '—').
    const label = getStateLabel(typeId, state)
        || columns.find(c => String(c.id) === String(columnId))?.title
        || (state ? String(state) : '')
        || '—';
    const targets = (!isArchived && state) ? getTransitionTargets(typeId, state, taskId) : [];

    if (!targets.length) {
        return (
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-slate-100 text-slate-800">
                {label}
            </span>
        );
    }

    const toggle = (e) => {
        e?.stopPropagation?.();
        if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setCoords({ top: r.bottom + 4, left: r.left });
        }
        setOpen(o => !o);
    };

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onClick={toggle}
                onPointerDown={e => e.stopPropagation()}
                aria-haspopup="listbox"
                aria-expanded={open}
                title="Changer le statut"
                className="px-2 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full bg-slate-100 text-slate-800 hover:bg-slate-200 cursor-pointer"
            >
                {label}
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            {open && coords && createPortal(
                <ul
                    ref={menuRef}
                    role="listbox"
                    style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 9999 }}
                    className="bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[176px] max-h-60 overflow-y-auto"
                    onPointerDown={e => e.stopPropagation()}
                >
                    {targets.map(t => (
                        <li
                            key={t.to}
                            role="option"
                            aria-selected={false}
                            onClick={e => { e.stopPropagation(); setOpen(false); document.dispatchEvent(new CustomEvent('kanbanTransitionRequest', { detail: { taskId, toState: t.to } })); }}
                            className="px-3 py-1.5 text-xs text-slate-700 hover:bg-iris-50 hover:text-iris-700 cursor-pointer"
                        >
                            {t.label}
                        </li>
                    ))}
                </ul>,
                document.body,
            )}
        </>
    );
};


/**
 * L'élément reactive-json : évalue les références YAML puis rend le contrôle.
 * Sur une fiche CRM, `~.data.*` désigne la ligne courante de la collection.
 */
export const TaskStateSelect = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const ev = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext });

  return (
    <TaskStateDropdown
      taskId={ev(props.taskId)}
      typeId={ev(props.taskType)}
      state={ev(props.currentState)}
      isArchived={Boolean(ev(props.isArchived))}
    />
  );
};

export default TaskStateDropdown;
